<template>
  <div class="h-100">
    <base-dialog
        ref="baseDialog"
        :title="dialog?.title"
        :modal="dialog?.modal"
        :ok-title="dialog?.okTitle"
        :cancel-title="dialog?.cancelTitle"
        :show-cancel-button="dialog?.showCancelButton"
        @confirm="handleDialogConfirm"
    >
      <span v-html="dialog && dialog.content" />
    </base-dialog>
    <b-container class="h-100 interaction-container pt-lg-3">
      <scroll-indicator
        class="d-lg-none"
        v-if="scrollable"
        :variant="this.colorVariant"
        :key="'si-' + currentItemNumber"
      />
      <app-loader v-if="appLoading" variant="bq-green" />
      <!-- tabindex to make container focusable and capture keyup event -->
      <div
        v-if="!appLoading"
        class="interaction-container__content"
        :class="containerFullHeight"
      >
        <div
          class="interaction-container__main"
          :class="containerFullHeight"
          tabindex="0"
          @keyup.enter="handleNext"
          ref="interactionItemContainer"
        >
          <b-row v-if="!hideProgressBarComplete" class="interaction-container__header mx-auto mb-lg-4 mt-2 mt-lg-0" align-v="center">
            <b-col>
              <progress-bar
                v-if="interactionItems && !progressHidden && !progressHiddenItem"
                :progress="interactionProgressRelative"
              />
              <logo
                v-if="progressHidden && !progressHiddenItem"
                :whitelabel-config="whitelabelConfig"
              />
            </b-col>
          </b-row>
          <app-loader v-if="itemLoading" />
          <b-row
            v-show="!itemLoading"
            class="interaction-container__row mx-auto"
            :class="containerFullHeight + ' ' + containerMargined"
            align-v="center"
          >
            <b-col :class="containerFullHeight">
              <!-- holds different containers for different classes of items e.g. OT or normal item -->
              <component
                :is="currentContainer"
                v-bind="currentContainerProps"
                @answer="handleAnswer"
                @answered="resetData"
                @ctrlButtonHidden="handleCtrlButtonHidden"
                @resetAfterDataTransmissionError="resetAfterDataTransmissionError"
              />
            </b-col>
          </b-row>
        </div>
        <div v-if="!hideControlButton" class="w-100 interaction-container__ctrl-button-container" fluid>
          <b-row class="h-100" align-v="center" align-h="center">
            <b-col cols="8" sm="6" md="4">
              <control-button
                class="w-100 interaction-container__ctrl-button"
                v-if="!isLastItem"
                :isLoading="isLoading"
                :renderKey="currentItemNumber"
                :disabled="!itemAnswered"
                @click="handleNext"
              >
                {{ $t('INTERACTION_BASE_NEXT').toUpperCase() }}
              </control-button>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-container>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { PROGRESS_HIDDEN } from '@/constants/items'
import { OUTCOME_TASK, IAT_TASK } from '@/constants/itemTypes'
import { TEXT_TYPES } from '@/constants/i18n'

import InteractionContainerMixin from '@/containers/Interaction/mixins/InteractionContainerMixin'
import ItemContainer from '@/containers/Items/ItemContainer'
import OutcomeTaskContainer from '@/containers/Items/OutcomeTaskContainer'
import IATContainer from '@/containers/Items/IATContainer'

import baseDialog from '@/components/ui/baseDialog'
import controlButton from '@/components/ui/controlButton'
import progressBar from '@/components/ui/progressBar'
import scrollIndicator from '@/components/ui/scrollIndicator'
import { INTERACTION_STATUS } from '@/constants/interaction'
import * as interactionService from '@/services/interaction'
import logo from '@/components/ui/logo'

export default {
  name: 'MainInteractionContainer',
  components: {
      'base-dialog': baseDialog,
      'control-button': controlButton,
      'progress-bar': progressBar,
      'scroll-indicator': scrollIndicator,
      logo
  },
  mixins: [InteractionContainerMixin],
  watch: {
    currentItemNumber() {
      setTimeout(this.scroll, 150)
    },
    dialog(newVal) {
      if(newVal) {
        this.$refs.baseDialog.show();
      }
    }
  },
  data() {
    return {
      scrollable: false,
      hideControlButton: false,
      hideProgressBarComplete: false,
      dialogConfirmed: false,
    }
  },
  computed: {
    ...mapGetters([
      'appLoading',
      'dialog',
      'interactionItems',
      'currentItemNumber',
      'currentItem',
      'interactionProgressRelative',
      'itemLoading',
      'isLoading',
      'colorVariant',
      'whitelabelConfig',
      'locale'
    ]),
    currentContainer() {
      const itemType = this.currentItem && this.currentItem.itemType

      this.hideProgressBarComplete = false
      this.hideControlButton = false

      switch(itemType) {
        case OUTCOME_TASK: return OutcomeTaskContainer
        case IAT_TASK: {
          this.hideProgressBarComplete = true
          this.hideControlButton = true
          return IATContainer
        }
        default: return ItemContainer
      }
    },

    currentContainerProps() {
      return {
        item: this.currentItem,
        interactionInstanceToken: this.interactionInstanceToken,
        organizationType: this.interactionInstance.organizationType,
        nextClicked: this.nextClicked,
        show: this.itemLoading,
      }
    },
    progressHidden() {
      return this.interactionInstance.progressHidden
    },
    progressHiddenItem() {
      return this.currentItem.properties.find(prop => prop.name === PROGRESS_HIDDEN)
    },
    containerFullHeight() {
      // if we step out of the usual UI layout with progressbar and control button hidden, we set the container height to 100%
      if(this.hideProgressBarComplete && this.hideControlButton) {
        return 'h-100 interaction-container__padded-0'
      }

      return ''
    },
    containerPadded() {
      return !this.hideProgressBarComplete && !this.hideControlButton ? 'interaction-container__padding-top' : 'interaction-container__padded-0'
    },
    containerMargined() {
      return !this.hideProgressBarComplete && !this.hideControlButton ? 'mb-md-5' : ''
    }
  },
  methods: {
    ...mapActions([
      'closeDialog',
      'fetchTranslations'
    ]),
    scroll() {
      const padding = 80
      this.scrollable = this.$refs.interactionItemContainer.scrollHeight - padding > document.body.clientHeight - padding
    },
    handleCtrlButtonHidden(value) {
      this.hideControlButton = value
    },
    handleDialogConfirm() {
      this.dialog && this.dialog.onConfirm()
    }
  },
  async mounted() {
    setTimeout(this.scroll, 150)
    /**
     * 
     * 
     * WORKAROUND: only used for chained IU study
     * to be removed afterwards
     */
    if(this.isLastItem && this.interactionInstance.status === INTERACTION_STATUS.RESULTS_AVAILABLE) {
      const externalUserToken = this.interactionInstance.externalRef

      if(this.interactionInstance.slug === 'IUBH_PERSONALITY') {
        // if fountainMap for externalUserToken has only one interaction feedback was not given follow up has not been started 
        // -> we let user go to results again from last item and give feedback

        // if fountainMap has 2 interactions follow up was opened (we have feedback for first)
        // we check if follow-up has feedback
        // if yes, we redirect to IUBH_COMPLETE
        // if no, we redirect to follow-up interaction
        const fountainMap = await interactionService.fetchFountainMap(externalUserToken)
        if(fountainMap.length > 1) {
          fountainMap.sort((a, b) => a.interactionInstanceId - b.interactionInstanceId)

          const interactionInstanceIdFollowUp = fountainMap[1].interactionInstanceId
          const feedbackFollowUp = await interactionService.fetchInteractionInstanceData(interactionInstanceIdFollowUp)
          const feedbackGivenFollowUp = feedbackFollowUp.length > 0
          if(feedbackGivenFollowUp) {
            this.$router.push({
              name: 'IUBHComplete'
            })
          } else {
            await this.fetchTranslations({
              textType: TEXT_TYPES.DASHBOARD,
              filter: `${TEXT_TYPES.DASHBOARD}_IUBH_TYPE_FOUNTAIN`
            })

            window.location.href = this.$t(`${TEXT_TYPES.DASHBOARD}_IUBH_TYPE_FOUNTAIN`, {
              locale: this.locale.id,
              externalUserToken: externalUserToken
            })
          }
        }

      } else if(this.interactionInstance.slug === 'IUBH_IAT') {
          const fountainMap = await interactionService.fetchFountainMap(externalUserToken)
          if(fountainMap.length > 1) {
            fountainMap.sort((a, b) => a.interactionInstanceId - b.interactionInstanceId)
            const interactionInstanceIdFollowUp = fountainMap[1].interactionInstanceId
            const feedbackFollowUp = await interactionService.fetchInteractionInstanceData(interactionInstanceIdFollowUp)
            const feedbackGivenFollowUp = feedbackFollowUp.length > 0

            if(feedbackGivenFollowUp) {
              this.$router.push({
                name: 'IUBHComplete'
              })
            }
          }
      }

    }
    /**
     * 
     * 
     * 
     * 
     * 
     */
  },
};
</script>
<style lang="scss">
  .interaction-container {
    padding: 0 0 0 0;
  }

  .interaction-container__padding-top {
    padding: 10px 0 0 0;
  }

  .interaction-container__content {
    height: 100%;
  }

  .interaction-container__ctrl-button-container {
    z-index: 1000;
    height: 80px;
    margin-top: 30px;
    @media screen and (min-width: 992px) {
      position: relative;
      bottom: 80px
    }
    @media screen and (max-width: 767px) {
      background-color: #EEE;
      margin-top: initial;
      bottom: 0;
      left: 0;
      position: fixed;
    }
  }

  .interaction-container__padded-0 {
    padding-bottom: 0px !important;
  }

  .interaction-container__main {
    overflow: auto;
    padding-bottom: 80px;
    background: #FFF;
    @media screen and (min-width: 992px) {
      padding: 15px;
    }
  }

  .interaction-container__main:focus {
    outline: 0;
  }

  .interaction-container__header {
    margin-bottom: 12px;
    @media screen and (min-width: 992px) {
        padding: 0 10%;
    }
  }

  .interaction-container__row {
    @media screen and (min-width: 992px) {
        padding: 0 10%;
        min-height: 120px;
    }
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .3s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }
</style>
